import React, { useEffect, useRef, useState } from 'react';
import { Check, Pencil, Plus, Trash2, Truck, X } from 'lucide-react';
import { useSuppliersStore, type Supplier } from '../../../store/suppliersStore';

interface ThemeTokens {
  text: string;
  muted: string;
  border: string;
  inputBg: string;
  cardBg: string;
  rowHover: string;
}

interface ManageSuppliersPanelProps {
  isDark: boolean;
  theme: ThemeTokens;
  /** Compact mode for the dropdown modal */
  compact?: boolean;
}

const SupplierRow: React.FC<{
  supplier: Supplier;
  isDark: boolean;
  theme: ThemeTokens;
  onRename: (id: string, name: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
  padX?: string;
}> = ({ supplier, isDark, theme, onRename, onDelete, padX = 'px-3' }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(supplier.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  const startEdit = () => {
    setEditName(supplier.name);
    setConfirmDelete(false);
    setEditing(true);
    setTimeout(() => nameRef.current?.select(), 0);
  };

  const commitEdit = async () => {
    const name = editName.trim();
    if (!name) return;
    if (name === supplier.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onRename(supplier.id, name);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <div
      className={`group flex items-center gap-2.5 ${padX} py-2 transition-colors`}
      style={{ borderBottom: `1px solid ${theme.border}` }}
      onMouseEnter={e => {
        if (!editing) (e.currentTarget as HTMLElement).style.backgroundColor = theme.rowHover;
      }}
      onMouseLeave={e => {
        if (!editing) (e.currentTarget as HTMLElement).style.backgroundColor = '';
      }}
    >
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: isDark ? '#374151' : '#e5e7eb' }}
      >
        <Truck size={12} style={{ color: theme.muted }} />
      </div>

      {editing ? (
        <input
          ref={nameRef}
          type="text"
          value={editName}
          onChange={e => setEditName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') void commitEdit();
            if (e.key === 'Escape') setEditing(false);
          }}
          disabled={saving}
          className="flex-1 text-sm px-2 py-1 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
          style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
          placeholder="Supplier name…"
        />
      ) : (
        <p className="flex-1 min-w-0 text-sm truncate" style={{ color: theme.text }}>
          {supplier.name}
        </p>
      )}

      {editing ? (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => void commitEdit()}
            disabled={!editName.trim() || saving}
            className="p-1.5 rounded transition-colors disabled:opacity-40"
            style={{ backgroundColor: isDark ? '#1a3a1a' : '#dcfce7', color: '#16a34a' }}
            title="Save"
          >
            <Check size={13} />
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="p-1.5 rounded transition-colors"
            style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6', color: theme.muted }}
            title="Cancel"
          >
            <X size={13} />
          </button>
        </div>
      ) : confirmDelete ? (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={async () => {
              setSaving(true);
              await onDelete(supplier.id);
              setSaving(false);
              setConfirmDelete(false);
            }}
            disabled={saving}
            className="text-[10px] px-2 py-0.5 rounded bg-red-500/80 hover:bg-red-600 text-white transition-colors disabled:opacity-50"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            className="text-[10px] px-2 py-0.5 rounded transition-colors"
            style={{ color: theme.muted, backgroundColor: isDark ? '#2b3140' : '#f3f4f6' }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={startEdit}
            className="p-1.5 rounded transition-colors hover:opacity-80"
            style={{ color: theme.muted }}
            title="Edit"
          >
            <Pencil size={13} />
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="p-1.5 rounded transition-colors hover:text-red-400"
            style={{ color: theme.muted }}
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

/** Shared add/edit/delete list for suppliers (settings + Edit Options modal). */
export const ManageSuppliersPanel: React.FC<ManageSuppliersPanelProps> = ({
  isDark,
  theme,
  compact = false,
}) => {
  const {
    suppliers,
    isLoading,
    error,
    fetchSuppliers,
    addSupplier,
    updateSupplier,
    removeSupplier,
  } = useSuppliersStore();

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const newNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    if (adding) newNameRef.current?.focus();
  }, [adding]);

  const submitAdd = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving(true);
    const ok = await addSupplier(name);
    setSaving(false);
    if (ok) {
      setNewName('');
      setAdding(false);
    }
  };

  const cancelAdd = () => {
    setAdding(false);
    setNewName('');
  };

  const padX = compact ? 'px-3' : 'px-5';

  return (
    <div>
      {error && (
        <div
          className={`${padX} py-2 text-xs`}
          style={{ color: '#fca5a5', borderBottom: `1px solid ${theme.border}` }}
        >
          {error}
        </div>
      )}

      {isLoading && suppliers.length === 0 && (
        <div className={`${padX} py-6 text-center text-xs`} style={{ color: theme.muted }}>
          Loading…
        </div>
      )}

      {!isLoading && suppliers.length === 0 && !adding && (
        <div className={`${padX} py-6 text-center`}>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center mx-auto mb-2"
            style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6' }}
          >
            <Truck size={16} style={{ color: theme.muted }} />
          </div>
          <p className="text-sm font-medium mb-0.5" style={{ color: theme.text }}>
            No suppliers yet
          </p>
          <p className="text-xs" style={{ color: theme.muted }}>
            Add suppliers below to use them when ordering.
          </p>
        </div>
      )}

      <div className={compact ? 'max-h-56 overflow-y-auto custom-scrollbar' : ''}>
        {suppliers.map(supplier => (
          <SupplierRow
            key={supplier.id}
            supplier={supplier}
            isDark={isDark}
            theme={theme}
            onRename={updateSupplier}
            onDelete={removeSupplier}
            padX={padX}
          />
        ))}
      </div>

      {adding ? (
        <div
          className={`flex items-center gap-2.5 ${padX} py-2.5`}
          style={{ borderTop: suppliers.length > 0 ? `1px solid ${theme.border}` : undefined }}
        >
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: isDark ? '#374151' : '#e5e7eb' }}
          >
            <Truck size={12} style={{ color: theme.muted }} />
          </div>
          <input
            ref={newNameRef}
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void submitAdd();
              if (e.key === 'Escape') cancelAdd();
            }}
            disabled={saving}
            className="flex-1 text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
            style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
            placeholder="Supplier name…"
          />
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => void submitAdd()}
              disabled={!newName.trim() || saving}
              className="p-1.5 rounded transition-colors disabled:opacity-40"
              style={{ backgroundColor: isDark ? '#1a3a1a' : '#dcfce7', color: '#16a34a' }}
              title="Add supplier"
            >
              <Check size={13} />
            </button>
            <button
              type="button"
              onClick={cancelAdd}
              className="p-1.5 rounded transition-colors"
              style={{ backgroundColor: isDark ? '#2b3140' : '#f3f4f6', color: theme.muted }}
              title="Cancel"
            >
              <X size={13} />
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`${padX} py-2`}
          style={{ borderTop: suppliers.length > 0 ? `1px solid ${theme.border}` : undefined }}
        >
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 text-xs rounded px-2 py-1.5 transition-colors"
            style={{ color: '#3b82f6' }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? '#1d2535' : '#eff6ff';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            <Plus size={13} />
            Add supplier
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageSuppliersPanel;
